import type { Tier, FileData, QuoteFormState, Service, TierData } from './types';
import type { QuoteFlowConfig } from '../shared/quoteFlow';
import { DEFAULT_QUOTE_FLOW_CONFIG, normalizeQuoteFlowConfig } from '../shared/quoteFlow';
import {
  calculateDurationTranslationCost,
  calculateTextTranslationCost,
  calculateWordTierRate,
} from '../shared/translationPricing';
import { getDurationQuantity, getPageQuantity, getServiceQuantity, getWordQuantity } from './utils/pricingUnits';

export const VALIDATION_THRESHOLDS = {
  MIN_WORDS_PER_PAGE: 200,
  MAX_WORDS_PER_PAGE: 600,
  MIXED_CONTENT_RATIO: 0.3
};

// Per-file limit. Keep in sync with the order-uploads bucket file_size_limit.
export const MAX_FILE_SIZE = 50 * 1024 * 1024;
export const MAX_FILES_HOME = 1;
export const MAX_FILES_QUOTE = 10;

export const SUPPORTED_EXTENSIONS = {
  docs: ['pdf', 'doc', 'docx', 'txt', 'rtf'],
  audio: ['mp3', 'wav', 'ogg', 'flac'],
  video: ['mp4', 'webm', 'mov', 'avi'],
  subtitles: ['srt'],
  image: ['jpg', 'jpeg', 'png', 'webp']
};

export const TIER_CONFIG = {
    machine: { label: 'Standard', desc: 'Machine translation + Light post-editing', multiplier: 0.5, priceMultiplier: 0.5 },
    professional: { label: 'Professional', desc: 'Native professional translator', multiplier: 1.0, priceMultiplier: 1.0 },
    specialist: { label: 'Specialist', desc: 'Subject matter expert (Legal/Medical/Technical)', multiplier: 1.5, priceMultiplier: 1.5 }
};

export const isFileSupported = (file: File) => {
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  const allSupported = [
    ...SUPPORTED_EXTENSIONS.docs,
    ...SUPPORTED_EXTENSIONS.audio,
    ...SUPPORTED_EXTENSIONS.video,
    ...SUPPORTED_EXTENSIONS.subtitles,
    ...SUPPORTED_EXTENSIONS.image
  ];
  return allSupported.includes(ext);
};

export const calculateTierRate = (
  tier: Tier,
  sourceLang: string,
  targetLang: string,
  SERVICE_LANGUAGES: { code: string; name: string; pricePerWord: number; pricePerMinute: number; minimumWordPrice?: number; minimumMinutePrice?: number }[],
  tiers: TierData[],
) => calculateWordTierRate(tier, sourceLang, targetLang, SERVICE_LANGUAGES, tiers);

export const getFileType = (file: File): string => {
  if (file.type.startsWith('video') || file.name.match(/\.(mp4|webm|mov|avi|mkv)$/i)) return 'video';
  if (file.type.startsWith('audio') || file.name.match(/\.(mp3|wav|m4a|ogg|flac|aac|wma)$/i)) return 'audio';
  if (file.type.startsWith('image') || file.name.match(/\.(jpg|jpeg|png|webp)$/i)) return 'image';
  return 'text';
};

export const calculateFileCost = (
  f: FileData,
  SERVICE_LANGUAGES: { code: string; name: string; pricePerWord: number; pricePerMinute: number; minimumWordPrice?: number; minimumMinutePrice?: number }[],
  availableServices: Service[],
  tiers: TierData[],
  quoteFlowConfig: QuoteFlowConfig = DEFAULT_QUOTE_FLOW_CONFIG,
) => {
  const effectiveType = (f.type === 'image' && f.ocrStatus === 'completed') ? 'text' : f.type;
  const options = availableServices.filter(s => s.category.includes(effectiveType as any));
  const normalizedQuoteFlowConfig = normalizeQuoteFlowConfig(quoteFlowConfig);

  let translationCost = 0;
  let servicesCost = 0;

  if (f.flatFee !== undefined && f.flatFee !== null && !f.classificationOverridden) {
    const allServiceIds = Array.from(new Set([...(f.services || []), ...(f.mandatoryServices || [])]));
    allServiceIds.forEach(svcId => {
      const svc = options.find(o => o.id === svcId);
      if (svc) {
        const quantity = getServiceQuantity(f, svc.unit);
        servicesCost += svc.base_price * quantity;
      }
    });

    return {
      translationCost: Number(f.flatFee || 0),
      servicesCost,
      total: Number(f.flatFee || 0) + servicesCost
    };
  }

  if (f.type === 'text' || f.type === 'image') {
    if (f.requiresCustomQuote) {
      translationCost = 0;
    } else {
      translationCost = calculateTextTranslationCost({
        wordCount: getWordQuantity(f),
        fileName: f.file?.name || f.storageFileName,
        pageCount: getPageQuantity(f),
        sourceLang: f.sourceLang,
        targetLang: f.targetLang,
        tier: f.tier,
        languages: SERVICE_LANGUAGES,
        tiers,
        minimumChargedWords: normalizedQuoteFlowConfig.fallbackPricing.minimumChargedWords,
      }).total;
    }
  } else if (f.type === 'audio' || f.type === 'video') {
    translationCost = calculateDurationTranslationCost({
      durationMinutes: getDurationQuantity(f),
      sourceLang: f.sourceLang,
      targetLang: f.targetLang,
      tier: f.tier,
      languages: SERVICE_LANGUAGES,
      tiers,
      minimumChargedMinutes: normalizedQuoteFlowConfig.fallbackPricing.minimumChargedMinutes,
    }).total;
  }

  const allServiceIds = Array.from(new Set([...f.services, ...f.mandatoryServices]));

  allServiceIds.forEach(svcId => {
    const svc = options.find(o => o.id === svcId);
    if (svc) {
      const quantity = getServiceQuantity(f, svc.unit);
      servicesCost += svc.base_price * quantity;
    }
  });

  return {
    translationCost,
    servicesCost,
    total: translationCost + servicesCost
  };
};

export const calculatePrice = (
  files: FileData[],
  formState: QuoteFormState,
  SERVICE_LANGUAGES: { code: string; name: string; pricePerWord: number; pricePerMinute: number; minimumWordPrice?: number; minimumMinutePrice?: number }[],
  availableServices: Service[],
  tiers: TierData[],
  deliveryOptions: Record<string, any>,
  quoteFlowConfig: QuoteFlowConfig = DEFAULT_QUOTE_FLOW_CONFIG,
) => {
  let total = 0;

  files.forEach(f => {
    const costs = calculateFileCost(f, SERVICE_LANGUAGES, availableServices, tiers, quoteFlowConfig);
    total += costs.total;
  });

  // Check if any file has services requiring physical document delivery
  const hasPhysicalDocServices = files.some(f => 
    f.services.some(sId => availableServices.find(s => s.id === sId)?.requires_physical_document === true)
  );
  const requiresPhysical = formState.delivery.requiresPhysical || hasPhysicalDocServices;
  if (requiresPhysical && formState.delivery.deliveryMethod && deliveryOptions[formState.delivery.deliveryMethod]) {
    const deliveryCost = deliveryOptions[formState.delivery.deliveryMethod].price;
    total += deliveryCost;
  }

  return total;
};

export const calculateEstimatedTime = (files: FileData[], formState: QuoteFormState, deliveryOptions: Record<string, any>, availableServices: Service[] = []) => {
  let maxProductionDays = 0;
  files.forEach(f => {
    let days = Math.ceil(f.wordCountEstimate / 2000);
    if (days < 1) days = 1;

    if (f.tier === 'specialist') days *= 1.5;
    if (f.services.includes('edit')) days *= 1.3;

    // Check if file has services requiring physical documents (add processing time)
    const hasPhysicalDocService = f.services.some(sId => 
      availableServices.find(s => s.id === sId)?.requires_physical_document === true
    );
    if (hasPhysicalDocService) {
      days += 2; 
    }

    if (days > maxProductionDays) maxProductionDays = days;
  });

  let shippingDays = 0;
  if (formState.delivery.requiresPhysical && formState.delivery.deliveryMethod && deliveryOptions[formState.delivery.deliveryMethod]) {
    const deliveryTimeStr = deliveryOptions[formState.delivery.deliveryMethod].time_estimate;
    const deliveryDays = parseInt(deliveryTimeStr.match(/\d+/g)?.pop() || '0');
    shippingDays = deliveryDays;
  }

  return { production: Math.ceil(maxProductionDays), shipping: shippingDays };
};
